import React, { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  CircularProgress,
} from '@mui/material';

/**
 * Six-digit email MFA code dialog (Conductor-style).
 */
const MFADialog = ({ open, email, onVerify, onCancel, verifying, error }) => {
  const [digits, setDigits] = useState(['', '', '', '', '', '']);
  const refs = useRef([]);

  useEffect(() => {
    if (open) {
      setDigits(['', '', '', '', '', '']);
      setTimeout(() => refs.current[0]?.focus(), 100);
    }
  }, [open]);

  const code = digits.join('');

  const setDigit = (index, value) => {
    const v = value.replace(/\D/g, '').slice(-1);
    const next = [...digits];
    next[index] = v;
    setDigits(next);
    if (v && index < 5) {
      refs.current[index + 1]?.focus();
    }
  };

  const onKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      refs.current[index - 1]?.focus();
    }
  };

  const onPaste = (e) => {
    const text = (e.clipboardData?.getData('text') || '').replace(/\D/g, '').slice(0, 6);
    if (text.length === 6) {
      e.preventDefault();
      setDigits(text.split(''));
      refs.current[5]?.focus();
    }
  };

  const submit = () => {
    if (code.length === 6) {
      onVerify(code);
    }
  };

  return (
    <Dialog open={open} onClose={verifying ? undefined : onCancel} maxWidth="xs" fullWidth>
      <DialogTitle>Verify your identity</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Enter the 6-digit code sent to {email || 'your email'}.
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', mb: 1 }} onPaste={onPaste}>
          {digits.map((d, i) => (
            <TextField
              key={i}
              inputRef={(el) => {
                refs.current[i] = el;
              }}
              value={d}
              onChange={(e) => setDigit(i, e.target.value)}
              onKeyDown={(e) => onKeyDown(i, e)}
              inputProps={{
                maxLength: 1,
                inputMode: 'numeric',
                style: { textAlign: 'center', fontSize: 20, width: 36 },
              }}
              disabled={verifying}
              size="small"
            />
          ))}
        </Box>
        {error && (
          <Typography color="error" variant="body2" sx={{ mt: 1 }}>
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={verifying}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={verifying || code.length !== 6}
          startIcon={verifying ? <CircularProgress size={16} color="inherit" /> : null}
        >
          Verify
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MFADialog;
